import React, { useContext, useState, useEffect } from 'react';
import { UserContext } from '../../UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import FormActionButton from '../../components/FormActionButton';
import { filterTimeSlots } from '../../lib/filterTimeSlots';
import AvailableSlots from './AvailableSlots';
import { AvailableSlot, ProviderProfile } from '../../apiClient';
import { useApi } from '../../ApiContext';
import ProviderProfileView from '../../components/ProviderProfileView';
import LoadingSpinner from '../../components/LoadingSpinner';
import Container from '../../components/Container';
import Heading2 from '../../components/Heading2';

const ChooseTimeSlotScreen: React.FC = () => {
  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();

  const [slots] = useState((location?.state.slots as AvailableSlot[]) || []);
  const [filteredSlots, setFilteredSlots] = useState<AvailableSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);

  const [loadingProviderData, setLoadingProviderData] = useState<boolean>(false);
  const [providerData, setProviderData] = useState<ProviderProfile | null>(null);

  useEffect(() => {
    if (userContext) {
      // On first load, try to get the selected slot and find associated provider data
      const { timeSlot, provider_id } = userContext.userData;
      const found = slots.find((slot) => slot.datetime === timeSlot && slot.providerId === provider_id);
      setSelectedSlot(found || null);
      if (found) {
        fetchProviderData(found.providerId);
      }
    }
  }, []);

  useEffect(() => {
    var result: AvailableSlot[] = [];
    if (slots) {
      result = filterTimeSlots(slots);
    }
    setFilteredSlots(result);
  }, [slots]);

  if (!userContext) return null;

  const { userData, setUserData } = userContext;

  const handleSelectionChange = (slot: AvailableSlot | null) => {
    setUserData({ ...userData, timeSlot: slot?.datetime, provider_id: slot?.providerId });
    setSelectedSlot(slot);
    if (slot) {
      fetchProviderData(slot.providerId);
    }
  };

  const handleTimeSlotSelection = async () => {
    setUserData({ ...userData, timeSlot: selectedSlot?.datetime, provider_id: selectedSlot?.providerId });
    navigate('/enter-email');
  };

  const fetchProviderData = async (providerId: string) => {
    if (providerId === providerData?.id) return;

    setLoadingProviderData(true);

    try {
      const response = await api.client.getProviderProfile(providerId);
      setProviderData(response.data);
      setUserData({ ...userData, providerProfile: response.data as ProviderProfile });
    } catch (error) {
      // Nothing to show the user when an error occurs.
      setProviderData(null);
    } finally {
      setLoadingProviderData(false);
    }
  };

  const selected = filteredSlots.find((slot) => slot.datetime === selectedSlot?.datetime) || null;

  const ProviderView = ({
    isLoading,
    isSlotSelected,
    providerData,
  }: {
    isLoading: boolean;
    isSlotSelected: boolean;
    providerData: ProviderProfile | null;
  }) => {
    return (
      <div className="flex justify-center mt-8">
        {isLoading ? (
          <LoadingSpinner darkMode={false} />
        ) : providerData ? (
          <ProviderProfileView
            name={providerData.fullName}
            credentials={providerData.credentials}
            bio={providerData.bio}
            imageUrl={providerData.image}
          />
        ) : selectedSlot ? (
          // No provider information available even though a slot is selected
          <></>
        ) : (
          // No time selected and no provider data available
          <div>Select a time slot to see your provider's name.</div>
        )}
      </div>
    );
  };

  return (
    <Container>
      <Heading2>First Available Appointments</Heading2>
      <p>
        We've looked for providers in your area who have availability on your preferred date and offer the specific
        treatment you selected.
      </p>
      {filteredSlots.length > 0 ? (
        <div>
          <AvailableSlots slots={filteredSlots} selectedSlot={selected} onChange={handleSelectionChange} />
          <div className="flex justify-center mt-8">
            <p>
              Looking for a specific provider?{' '}
              <a className="font-bold text-decoration-line: underline;" href="https://book.skinclique.com">
                You can search for them here!
              </a>
            </p>
          </div>{' '}
          <ProviderView isLoading={loadingProviderData} isSlotSelected={!!selectedSlot} providerData={providerData} />
          <div className="flex justify-center mt-8">
            <FormActionButton onClick={handleTimeSlotSelection} disabled={!selectedSlot} isLoading={false}>
              Continue to Booking
            </FormActionButton>
          </div>
        </div>
      ) : (
        <p className="mb-8 text-center">It doesn't look like we have any available providers for the date selected.</p>
      )}
    </Container>
  );
};

export default ChooseTimeSlotScreen;
